import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, EyeOff, Eye, Save, X } from "lucide-react";
import { adminList, adminCreate, adminUpdate, adminDelete } from "../../lib/content";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { ImageUpload } from "./ImageUpload";
import { RESOURCE_CONFIG } from "./resourceConfig";
import { toast } from "sonner";

function defaultsFor(config) {
  const def = {};
  for (const f of config.fields) {
    if (f.type === "switch") def[f.key] = f.default ?? true;
    else if (f.type === "list") def[f.key] = [];
    else if (f.type === "number") def[f.key] = 0;
    else def[f.key] = "";
  }
  return def;
}

const Field = ({ field, value, onChange }) => {
  if (field.type === "textarea") {
    return <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} rows={4} />;
  }
  if (field.type === "select") {
    return (
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
        <SelectContent>
          {field.options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  }
  if (field.type === "image") {
    return <ImageUpload value={value} onChange={onChange} testId={`upload-${field.key}`} />;
  }
  if (field.type === "list") {
    return (
      <Textarea
        value={(value || []).join("\n")}
        onChange={(e) => onChange(e.target.value.split("\n").filter((x) => x.trim() !== ""))}
        placeholder="One item per line"
        rows={4}
      />
    );
  }
  if (field.type === "switch") {
    return <Switch checked={!!value} onCheckedChange={onChange} />;
  }
  if (field.type === "number") {
    return <Input type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} />;
  }
  if (field.type === "date") {
    return <Input type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} />;
  }
  return <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} />;
};

export const ResourceManager = ({ resource }) => {
  const config = RESOURCE_CONFIG[resource];
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | item | "new"
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const list = await adminList(resource);
      setItems(list);
    } catch (e) {
      toast.error("Could not load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resource]);

  const startNew = () => { setDraft(defaultsFor(config)); setEditing("new"); };
  const startEdit = (item) => { setDraft({ ...item }); setEditing(item); };
  const close = () => { setEditing(null); setDraft(null); };

  const save = async () => {
    // basic required validation
    for (const f of config.fields) {
      if (f.required && (draft[f.key] === "" || draft[f.key] === null || draft[f.key] === undefined)) {
        toast.error(`${f.label.replace(" *", "")} is required`);
        return;
      }
    }
    setSaving(true);
    try {
      if (editing === "new") {
        await adminCreate(resource, draft);
        toast.success("Created");
      } else {
        await adminUpdate(resource, editing.id, draft);
        toast.success("Updated");
      }
      await load();
      close();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const del = async (item) => {
    if (!window.confirm(`Delete "${item.name || item.title || item.student_name || item.id}"?`)) return;
    try {
      await adminDelete(resource, item.id);
      toast.success("Deleted");
      setItems(items.filter((x) => x.id !== item.id));
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid={`manager-${resource}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h2 className="font-heading text-2xl font-bold text-[#0A192F]">{config.label}</h2>
          <p className="text-sm text-[#64748B] mt-0.5">{items.length} item{items.length === 1 ? "" : "s"}</p>
        </div>
        <Button onClick={startNew} className="bg-[#00B37E] hover:bg-[#009166] text-white rounded-full h-10" data-testid={`${resource}-add`}>
          <Plus className="w-4 h-4 mr-2" /> {config.addLabel}
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-[#64748B] py-8"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-slate-300 rounded-2xl text-[#64748B]">
          No items yet. Click <b>{config.addLabel}</b> to create one.
        </div>
      ) : (
        <div className="space-y-2" data-testid={`${resource}-list`}>
          {items.map((item) => {
            const card = config.list(item);
            return (
              <div key={item.id} className="bg-white border border-slate-200 rounded-xl p-3 md:p-4 flex items-center gap-4">
                {card.image ? (
                  <img src={card.image} alt="" className="w-14 h-14 rounded-lg object-cover flex-shrink-0" />
                ) : (
                  <div className="w-14 h-14 rounded-lg bg-slate-100 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="font-heading font-bold text-[#0A192F] truncate">{card.title}</div>
                    {card.badges?.map((b, i) => (
                      <span key={i} className="px-2 py-0.5 rounded-full bg-slate-100 text-[10px] uppercase tracking-wider font-bold text-[#0A192F]">{b}</span>
                    ))}
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold ${card.published ? "bg-[#00B37E]/15 text-[#00B37E]" : "bg-slate-100 text-slate-500"}`}>
                      {card.published ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                      {card.published ? "Live" : "Hidden"}
                    </span>
                  </div>
                  {card.subtitle ? <div className="text-xs text-[#64748B] mt-1 truncate">{card.subtitle}</div> : null}
                </div>
                <div className="flex gap-1.5 flex-shrink-0">
                  <Button onClick={() => startEdit(item)} variant="outline" size="sm" className="rounded-full h-9 px-3" data-testid={`${resource}-edit-${item.id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button onClick={() => del(item)} variant="outline" size="sm" className="rounded-full h-9 px-3 border-red-200 text-red-600 hover:bg-red-50" data-testid={`${resource}-delete-${item.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={editing !== null} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={`${resource}-dialog`}>
          <DialogHeader>
            <DialogTitle>{editing === "new" ? `New ${config.label.slice(0, -1)}` : `Edit ${config.label.slice(0, -1)}`}</DialogTitle>
          </DialogHeader>
          {draft ? (
            <div className="space-y-4 py-2">
              {config.fields.map((f) => (
                <div key={f.key} className={f.type === "switch" ? "flex items-center justify-between" : ""}>
                  <Label className="text-xs uppercase tracking-wider font-bold text-[#0A192F]">{f.label}</Label>
                  {f.type === "switch" ? (
                    <Field field={f} value={draft[f.key]} onChange={(v) => setDraft({ ...draft, [f.key]: v })} />
                  ) : (
                    <div className="mt-1.5">
                      <Field field={f} value={draft[f.key]} onChange={(v) => setDraft({ ...draft, [f.key]: v })} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={close}><X className="w-4 h-4 mr-2" /> Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#0A192F] hover:bg-[#0F2648] text-white" data-testid={`${resource}-save`}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {saving ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ResourceManager;
